// Row fills, label indents and negative-figure colours shared by every Actuals
// vs Budget table, so the three report levels present one visual language.
//
// Deliberately ONE language. An earlier revision carried a second, depth-shaded
// set alongside this one (department rows charcoal, cost centres mid-grey, budget
// lines light) that only the Full-details level used. Collapsed, that level shows
// department rows only, so the whole report read as a dark block while Department
// and Department + Cost Centre stayed light. Hierarchy is carried by indent and
// weight here; the only fills left are the section bands and the total rows.

import { ROW_BUDGET_LINE, ROW_COST_CENTRE, ROW_DEPARTMENT, ROW_PROJECT, ROW_TOTAL } from '../types/ReportRowType';

// Left edge of each time-base column group, so the eye can separate Month from
// Reporting period from Lifetime across a wide table. The separator's colour
// differs between the header bands, the body and the footer — see
// tables/_avb-report.scss, which owns all three.
export const GROUP_START_CELL = 'avb-report-table__group-start';

// Row fills: white data rows at every tier, and blue totals. The stylesheet reads
// `avb-report-table__total` to close a section (no hairline under a total row).
const REPORT_ROW_FILL = {
    [ROW_TOTAL]: '!tw-bg-blue !tw-text-white avb-report-table__total',
};

export const reportRowFillClass = (rowType) => REPORT_ROW_FILL[rowType] || '';

// Departments stay semibold so they read as the parent of everything under them;
// every tier below is normal, and the indent separates them. Nothing below a
// department may be heavier than its own parent, or the hierarchy inverts.
const REPORT_ROW_WEIGHT = {
    [ROW_DEPARTMENT]: 'tw-font-semibold',
    [ROW_COST_CENTRE]: 'tw-font-normal',
    [ROW_PROJECT]: 'tw-font-normal',
    [ROW_BUDGET_LINE]: 'tw-font-normal',
    [ROW_TOTAL]: 'tw-font-semibold',
};

export const reportRowWeightClass = (rowType) => REPORT_ROW_WEIGHT[rowType] || 'tw-font-normal';

// Label indent per tier, stepping monotonically down the hierarchy: departments
// and totals take the cell's own padding, then cost centre → project → budget
// line each step in from it. With no fills to separate the tiers, this is what
// the reader follows, so the steps must never collapse or reorder.
//
// Marked important because tables/_avb-report.scss's `tbody tr > td` padding rule
// is a two-class selector and would otherwise out-specify these utilities (the
// summary tables put the indent on the cell itself). Logical (`ps`) so the
// hierarchy indents from the right in Arabic.
const REPORT_ROW_INDENT = {
    [ROW_COST_CENTRE]: '!tw-ps-8',
    [ROW_PROJECT]: '!tw-ps-12',
    [ROW_BUDGET_LINE]: '!tw-ps-16',
};

export const reportRowIndentClass = (rowType) => REPORT_ROW_INDENT[rowType] || '';

// Only totals sit on a dark fill, so they alone need the lighter tints.
export const isDarkReportRow = (rowType) => rowType === ROW_TOTAL;

/**
 * Colour for a figure shown in parentheses. Parentheses alone carry the "this is
 * unfavourable" meaning (see the report legend); the colour reinforces it.
 * Neutral for zero and for non-numeric placeholders such as the comparison dash.
 *
 * @param {*} value
 * @param {string} rowType
 * @returns {string}
 */
export const reportNegativeColourClass = (value, rowType) => {
    const numeric = Number(value);

    if (!isFinite(numeric) || numeric >= 0) {
        return '';
    }

    return isDarkReportRow(rowType) ? 'tw-text-red-25' : 'tw-text-red';
};
